import {
    DOSSIER_STATUS_INCOMPLETE,
    DOSSIER_STATUS_DRAFT,
    DOSSIER_STATUS_IN_PROGRESS,
    DOSSIER_STATUS_TRANSMIT,
    DOSSIER_STATUS_AWAITING,
    DOSSIER_STATUS_DRAFT_CANCELLED,
    DOSSIER_STATUS_TRANSMITTED_CANCELLED,
    DOSSIER_STATUS_SUSPENDED,
    DOSSIER_STATUS_ARCHIVED,
    DOSSIER_STATUS_REGULS,
    DOSSIER_STATUS_REJECTED_S2,
    DOSSIER_STATUS_REJECTED_S4,
    DOSSIER_STATUS_DONE,
    DOSSIER_STATUS_VALIDATED,
} from '@europrocurement/l2d-modules/modules/Dossiers/constants/status';
import {
    PROCESS_STATUS_FLAG_FAILED,
    PROCESS_STATUS_FLAG_DONE,
    PROCESS_STATUS_FLAG_ERROR,
    PROCESS_STATUS_FLAG_ONGOING,
    PROCESS_STATUS_FLAG_PENDING,
} from '@europrocurement/l2d-modules/modules/Dossiers/constants/validators';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
    faCircle,
    faCircleCheck,
    faCircleExclamation,
    faCirclePause,
    faCircleX,
    faClock,
    faSend,
} from '@fortawesome/pro-regular-svg-icons';
import { NamedIconsType } from '@europrocurement/l2d-icons';
import { type Palette, type PaletteColor, type SxProps, lighten } from '@mui/material';
import L2DTheme from '../Theme';

type StatusStyleType = {
    color: string;
    colorPalette: PaletteColor;
    paletteString: keyof Palette;
    icon: string;
    iconProp: IconProp;
    namedIcon: NamedIconsType;
    sxStatus: SxProps;
};

const defaultSxByColor = (color: string) => ({
    border: `1px solid ${lighten(color, 0.6)}`,
    color,
    background: `linear-gradient(to bottom, ${lighten(color, 0.8)},  ${lighten(color, 0.95)})`,
});

const useStatusStyle = () => {
    const theme = L2DTheme();

    const palettePrimary = theme.palette.primary as PaletteColor;
    const palettePurple = theme.palette.purple as PaletteColor;
    const paletteAmber = theme.palette.amber as PaletteColor;
    const paletteSuccess = theme.palette.success as PaletteColor;
    const paletteWarning = theme.palette.error as PaletteColor;
    const paletteGrey = theme.palette.blueGrey as PaletteColor;

    const primaryDark = palettePrimary.dark;
    const primaryMain = palettePrimary.main;
    const purple = palettePurple?.main || '#7A45E2';
    const amber = paletteAmber?.main || '#FF8229';
    const success = paletteSuccess.main;
    const warning = paletteWarning.main;
    const grey70 = theme.palette.grey[700];

    const STATUS_STYLE_TODO = {
        color: primaryDark,
        colorPalette: palettePrimary,
        paletteString: 'primary' as keyof Palette,
        icon: 'fa-circle',
        iconProp: faCircle,
        namedIcon: {
            name: 'status_todo',
            displayName: 'A faire',
            props: {
                icon: faCircle,
            },
        },
        sxStatus: defaultSxByColor(primaryDark),
    };
    const STATUS_STYLE_PROGRESS = {
        color: primaryMain,
        colorPalette: palettePrimary,
        paletteString: 'primary' as keyof Palette,
        icon: 'fa-send',
        iconProp: faSend,
        namedIcon: {
            name: 'status_progress',
            displayName: 'En cours',
            props: {
                icon: faSend,
            },
        },
        sxStatus: defaultSxByColor(primaryMain),
    };
    const STATUS_STYLE_WAITING = {
        color: purple,
        colorPalette: palettePurple,
        paletteString: 'purple' as keyof Palette,
        icon: 'fa-clock',
        iconProp: faClock,
        namedIcon: {
            name: 'status_waiting',
            displayName: 'En attente',
            props: {
                icon: faClock,
            },
        },
        sxStatus: defaultSxByColor(purple),
    };
    const STATUS_STYLE_ACTION = {
        color: amber,
        colorPalette: paletteAmber,
        paletteString: 'amber' as keyof Palette,
        icon: 'fa-circle-exclamation',
        iconProp: faCircleExclamation,
        namedIcon: {
            name: 'status_action',
            displayName: 'Action',
            props: {
                icon: faCircleExclamation,
            },
        },
        sxStatus: defaultSxByColor(amber),
    };
    const STATUS_STYLE_SUCCESS = {
        color: success,
        colorPalette: paletteSuccess,
        paletteString: 'success' as keyof Palette,
        icon: 'fa-circle-check',
        iconProp: faCircleCheck,
        namedIcon: {
            name: 'status_success',
            displayName: 'Succès',
            props: {
                icon: faCircleCheck,
            },
        },
        sxStatus: defaultSxByColor(success),
    };
    const STATUS_STYLE_DANGER = {
        color: warning,
        colorPalette: paletteWarning,
        paletteString: 'warning' as keyof Palette,
        icon: 'fa-circle-x',
        iconProp: faCircleX,
        namedIcon: {
            name: 'status_danger',
            displayName: 'Erreur',
            props: {
                icon: faCircleX,
            },
        },
        sxStatus: defaultSxByColor(warning),
    };
    const STATUS_STYLE_INACTIV = {
        color: grey70,
        colorPalette: paletteGrey,
        paletteString: 'blueGrey' as keyof Palette,
        icon: 'fa-circle-pause',
        iconProp: faCirclePause,
        namedIcon: {
            name: 'status_inactiv',
            displayName: 'Inactif',
            props: {
                icon: faCirclePause,
            },
        },
        sxStatus: defaultSxByColor(grey70),
    };

    // Mapping between status id and status style
    const dossierStatusStyles: Record<number, StatusStyleType> = {
        // A faire
        [DOSSIER_STATUS_DRAFT]: STATUS_STYLE_TODO,
        // progression
        [DOSSIER_STATUS_IN_PROGRESS]: STATUS_STYLE_PROGRESS,
        [DOSSIER_STATUS_TRANSMIT]: STATUS_STYLE_PROGRESS,
        [DOSSIER_STATUS_VALIDATED]: STATUS_STYLE_PROGRESS,
        // En attente
        [DOSSIER_STATUS_AWAITING]: STATUS_STYLE_WAITING,
        // 'Annulés'
        [DOSSIER_STATUS_DRAFT_CANCELLED]: STATUS_STYLE_INACTIV,
        [DOSSIER_STATUS_TRANSMITTED_CANCELLED]: STATUS_STYLE_INACTIV,
        [DOSSIER_STATUS_SUSPENDED]: STATUS_STYLE_INACTIV,
        [DOSSIER_STATUS_ARCHIVED]: STATUS_STYLE_INACTIV,
        // Nécéssite action
        [DOSSIER_STATUS_INCOMPLETE]: STATUS_STYLE_ACTION,
        [DOSSIER_STATUS_REGULS]: STATUS_STYLE_ACTION,
        // Terminé
        [DOSSIER_STATUS_DONE]: STATUS_STYLE_SUCCESS,
        // Erreur
        [DOSSIER_STATUS_REJECTED_S4]: STATUS_STYLE_DANGER,
        [DOSSIER_STATUS_REJECTED_S2]: STATUS_STYLE_DANGER,
    };

    // Mapping between status id and status style
    const validatorStatusStyles: Record<number, StatusStyleType> = {
        // A faire
        [PROCESS_STATUS_FLAG_PENDING]: STATUS_STYLE_TODO,
        // progression
        [PROCESS_STATUS_FLAG_ONGOING]: STATUS_STYLE_PROGRESS,
        // Nécéssite action
        [PROCESS_STATUS_FLAG_ERROR]: STATUS_STYLE_ACTION,
        // Terminé
        [PROCESS_STATUS_FLAG_DONE]: STATUS_STYLE_SUCCESS,
        // Erreur
        [PROCESS_STATUS_FLAG_FAILED]: STATUS_STYLE_DANGER,
    };

    const getDossierStatusStyle = (statusId: number): StatusStyleType => {
        if (dossierStatusStyles[statusId]) {
            return dossierStatusStyles[statusId];
        }

        // Default to STATUS_STYLE_INACTIV if statusId is not found
        console.log(`Statut dossier manquant : ${statusId}`);
        return STATUS_STYLE_INACTIV;
    };

    const getValidatorStatusStyle = (statusTag: number): StatusStyleType => {
        if (validatorStatusStyles[statusTag]) {
            return validatorStatusStyles[statusTag];
        }

        // Default to STATUS_STYLE_INACTIV if statusId is not found
        console.log(`Statut valideur manquant : ${statusTag}`);
        return STATUS_STYLE_INACTIV;
    };

    return {
        getDossierStatusStyle,
        getValidatorStatusStyle,
    };
};
export default useStatusStyle;
